import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Typography,
  ToastUtils,
  Checkbox,
} from '@forgeglobal/design-framework';
import { startOfYear, isValid } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { InputAdornment, TextField, FormHelperText } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import useDataAsOfDate from '@tcs/client-portal-common/hooks/useDataAsOfDate';
import useRepresentativeAccounts from '@tcs/client-portal-common/hooks/useRepresentativeAccounts';
import { useStyles } from './style';
import { getDateOnly } from '@tcs/client-portal-common/utils/formatDate';
import {
  ReportTimeframe,
  ReportType,
} from '@tcs/client-portal-api/types/ReportType';
import ReportTypeSelector from '../ReportTypeSelector';
import useGenerateReportMutation from '@tcs/client-portal-representative/hooks/useGenerateReportMutation';
import type { GenerateReportRequest } from '@tcs/client-portal-representative/hooks/useGenerateReportMutation';
import ConfirmationModal from '../ConfirmationModal';
import {
  getClientNames,
  getAccountIdsByClient,
  getAccounts,
} from '@tcs/client-portal-common/utils/accountUtils';
export interface GenerateReportModalProps {
  open: boolean;
  representativeCode: string;
  onClose: () => void;
}

const TIMEFRAME_OPTIONS = [
  { value: ReportTimeframe.LAST_YEAR, label: ReportTimeframe.LAST_YEAR },
  { value: ReportTimeframe.YEAR_TO_DATE, label: ReportTimeframe.YEAR_TO_DATE },
  {
    value: ReportTimeframe.CUSTOM_TIME_RANGE,
    label: ReportTimeframe.CUSTOM_TIME_RANGE,
  },
];
const GenerateReportModal: React.FC<GenerateReportModalProps> = ({
  open,
  representativeCode,
  onClose,
}) => {
  const classes = useStyles();

  const generateReportMutation = useGenerateReportMutation(representativeCode);

  const dataAsOfDate = useDataAsOfDate();
  const { data: accounts } = useRepresentativeAccounts(representativeCode);
  const clientNames = getClientNames(accounts);
  const allAccountIds = getAccounts(accounts);
  const [realAccounts, setRealAccounts] = useState([]);
  const [formData, setFormData] = useState<GenerateReportRequest>({
    clientNames: [],
    accounts: [],
    reportType: '',
    timeframe: '',
    fromDate: null,
    toDate: null,
    reportName: '',
  });

  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [accountSearchTerm, setAccountSearchTerm] = useState<string>('');

  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const [isGenerateReportSuccess, setIsGenerateReportSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const handleInputChange = (
    field: keyof GenerateReportRequest,
    value: any
  ) => {
    if (
      ((field === 'fromDate' || field === 'toDate') && !isValid(value)) ||
      (field === 'fromDate' && new Date(value) > formData.toDate)
    ) {
      ToastUtils.error('Invalid date. Please enter a valid date.', true);
      return;
    }
    if (field === 'reportType') {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        timeframe: '',
      }));
    } else if (field === 'timeframe') {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        fromDate:
          value === ReportTimeframe.CUSTOM_TIME_RANGE
            ? startOfYear(new Date())
            : null,
        toDate:
          value === ReportTimeframe.CUSTOM_TIME_RANGE ? dataAsOfDate : null,
        reportName: '',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleClose = () => {
    setFormData({
      clientNames: [],
      accounts: [],
      reportType: '',
      timeframe: '',
      reportName: '',
      fromDate: null,
      toDate: null,
    });
    setSelectedClients([]);
    setSelectedAccounts([]);
    setAccountSearchTerm('');
    onClose();
  };

  const handleCloseConfirmation = () => {
    if (isGenerateReportSuccess) {
      setIsConfirmationModalOpen(false);
      onClose();
    } else {
      setIsConfirmationModalOpen(false);
    }
  };
  useEffect(() => {
    if (selectedClients.length === 0) {
      accounts &&
        clientNames &&
        setRealAccounts(getAccountIdsByClient(accounts, clientNames));
      if (selectedAccounts.length === 0) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          clientNames: [],
          accounts: [],
        }));
      }
    }
    if (selectedClients.length == clientNames.length) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        clientNames: [],
      }));
    }
    if (selectedAccounts.length == allAccountIds.length) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        accounts: [],
      }));
    }
    if (selectedClients.length === 1 && selectedAccounts.length === 0) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        accounts: getAccountIdsByClient(accounts, selectedClients),
      }));
    }
  }, [selectedClients, selectedAccounts, accounts, clientNames, allAccountIds]);
  const handleSubmit = () => {
    generateReportMutation.mutate(formData, {
      onSuccess: () => {
        setMessage(
          'Your report is being generated. You will receive an email when it’s ready for download.'
        );
        setIsGenerateReportSuccess(true);
        setIsConfirmationModalOpen(true);
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.errors?.reportType?.[0] ||
          error?.response?.data?.title ||
          'Failed to generate report.';
        setMessage(message);
        setIsGenerateReportSuccess(false);
        setIsConfirmationModalOpen(true);
      },
    });
  };

  const isFormValid = useMemo(() => {
    if (formData.reportType === ReportType.TRANSACTION && !formData.timeframe)
      return false;
    if (formData.timeframe === ReportTimeframe.CUSTOM_TIME_RANGE) {
      if (!formData.fromDate || !formData.toDate || !formData.reportName) {
        return false;
      }
      if (formData.reportName && formData.reportName.trim().length > 100) {
        return false;
      }
      return formData.fromDate <= formData.toDate;
    }
    if (!formData.reportType || !formData.reportName) {
      return false;
    }
    return true;
  }, [formData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={handleClose}
        className={classes.dialog}
        disableEnforceFocus>
        <DialogTitle onClose={handleClose}>
          Generate a Custom Report
        </DialogTitle>
        <DialogContent className={classes.wrapper}>
          <Box className={classes.content}>
            <Typography variant="body1">
              After making your selections, click 'Generate Report.' It will
              appear in the Reports page when complete.
            </Typography>

            <Box className={classes.controls}>
              <FormControl>
                <Select
                  className={classes.select}
                  id="client-dropdown"
                  aria-label="Client"
                  multiple
                  disabled={
                    selectedAccounts.length > 0 && selectedClients.length == 0
                  }
                  value={selectedClients}
                  onChange={(e) => {
                    const value = e.target.value;
                    const selectedClientsArray =
                      typeof value === 'string'
                        ? value.split(',')
                        : (value as string[]);
                    setSelectedClients(selectedClientsArray);
                    if (selectedAccounts.length > 0) {
                      setSelectedAccounts([]);
                    }
                    if (selectedClientsArray.length > 1) {
                      setSelectedAccounts([]);
                    }
                    setFormData({
                      ...formData,
                      clientNames: selectedClientsArray,
                      accounts:
                        selectedClientsArray.length > 1
                          ? getAccountIdsByClient(
                              accounts,
                              selectedClientsArray
                            )
                          : selectedClientsArray.length == 1
                          ? allAccountIds
                          : selectedAccounts,
                    });

                    setRealAccounts(
                      getAccountIdsByClient(accounts, selectedClientsArray)
                    );
                  }}
                  displayEmpty
                  renderValue={(selected: unknown) => {
                    const selectedClientsArray = selected as string[];
                    if (
                      (!selectedClientsArray ||
                        selectedClientsArray.length === 0) &&
                      selectedClients.length === 0
                    ) {
                      return 'Select client(s)';
                    }
                    const clientLabels = selectedClients?.map((clientId) => {
                      const client = clientNames?.find(
                        (cli) => cli === clientId
                      );
                      return client;
                    });
                    return (
                      <Box className={classes.renderLabel}>
                        {clientLabels.join(', ')}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    MenuListProps: {
                      className: classes.menuList,
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                  onDelete={(value) => {
                    setSelectedClients(
                      selectedClients.filter((client) => client !== value)
                    );
                  }}>
                  <Box className={classes.clientSettings}>
                    <Typography
                      variant="body2"
                      className={classes.clientText}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          selectedClients.length === clientNames.length &&
                          clientNames.length > 0
                        ) {
                          setSelectedClients([]);
                          setRealAccounts([]);
                        } else {
                          setSelectedClients(clientNames);
                          setFormData((prevFormData) => ({
                            ...prevFormData,
                            clientNames:
                              selectedAccounts.length > 1 ? [] : clientNames,
                          }));
                          setRealAccounts(
                            getAccountIdsByClient(accounts, clientNames)
                          );
                        }
                      }}>
                      {clientNames.every((client: string) =>
                        selectedClients.includes(client)
                      )
                        ? 'Unselect all'
                        : 'Select all'}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={classes.clientText}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedClients([]);
                      }}>
                      Clear
                    </Typography>
                  </Box>
                  {clientNames?.map((client: string) => (
                    <MenuItem
                      key={client}
                      value={client}
                      className={classes.menuItem}>
                      <Checkbox
                        className={classes.checkbox}
                        checked={selectedClients.includes(client)}
                      />
                      {client}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  className={classes.select}
                  id="accounts"
                  aria-label="accounts"
                  disabled={selectedClients.length > 1}
                  multiple
                  value={selectedAccounts}
                  onChange={(e) => {
                    const value = e.target.value;
                    const selectedAccountsArray = value as string[];
                    setSelectedAccounts(selectedAccountsArray);
                    setFormData({
                      ...formData,
                      clientNames: selectedClients,
                      accounts: selectedAccountsArray,
                    });
                  }}
                  displayEmpty
                  renderValue={(selected: unknown) => {
                    const selectedArray = selected as string[];
                    if (!selectedArray || selectedArray.length === 0) {
                      return 'Select account(s)';
                    }
                    const accountLabels = selectedArray.map((val) => {
                      const account = realAccounts?.find(
                        (account) => account === val
                      );
                      return account;
                    });
                    return (
                      <Box className={classes.renderLabel}>
                        {accountLabels.join(', ')}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    MenuListProps: {
                      className: classes.menuList,
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                  onClose={() => {
                    setAccountSearchTerm('');
                  }}
                  onDelete={(value) => {
                    setSelectedAccounts(
                      selectedAccounts.filter((item) => item !== value)
                    );
                  }}>
                  <Box className={classes.clientSettings}>
                    <Typography
                      variant="body2"
                      className={classes.clientText}
                      onClick={(e) => {
                        e.stopPropagation();
                        const allAccountIds = realAccounts?.map(
                          (account) => account
                        );
                        if (
                          realAccounts?.every((account) =>
                            selectedAccounts.includes(account)
                          ) &&
                          realAccounts.length > 0
                        ) {
                          setSelectedAccounts([]);
                          setFormData({
                            ...formData,
                            accounts: [],
                          });
                        } else {
                          setSelectedAccounts(allAccountIds);
                          setFormData({
                            ...formData,
                            accounts: allAccountIds,
                          });
                        }
                      }}>
                      {realAccounts?.every((account) =>
                        selectedAccounts.includes(account)
                      )
                        ? 'Unselect all'
                        : 'Select all'}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={classes.clientText}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAccounts([]);
                        setFormData({
                          ...formData,
                          accounts: [],
                        });
                      }}>
                      Clear
                    </Typography>
                  </Box>
                  <TextField
                    className={classes.searchField}
                    fullWidth
                    size="small"
                    placeholder="Search"
                    value={accountSearchTerm}
                    onChange={(e) => setAccountSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {realAccounts
                    ?.filter((account) => {
                      if (!accountSearchTerm.trim()) return true;
                      const searchLower = accountSearchTerm.toLowerCase();
                      return account?.toLowerCase().includes(searchLower);
                    })
                    .map((account) => (
                      <MenuItem
                        key={account}
                        value={account}
                        className={classes.menuItem}>
                        <Checkbox
                          className={classes.checkbox}
                          checked={selectedAccounts.includes(account)}
                        />
                        {account}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl>
                <ReportTypeSelector
                  selectedReportType={formData.reportType as ReportType}
                  onReportTypeChange={(reportType: ReportType) =>
                    handleInputChange('reportType', reportType)
                  }
                />
              </FormControl>
            </Box>

            {formData.reportType === ReportType.TRANSACTION && (
              <Box className={classes.controls}>
                <FormControl>
                  <Select
                    error={!formData.timeframe}
                    className={classes.selectTimeframe}
                    id="timeframe"
                    aria-label="Timeframe"
                    value={formData.timeframe}
                    onChange={(e) =>
                      handleInputChange('timeframe', e.target.value)
                    }
                    displayEmpty
                    renderValue={(selected: unknown) => {
                      if (!selected || selected === '') {
                        return 'Select timeframe';
                      }
                      const selectedValue = selected as string;
                      return (
                        TIMEFRAME_OPTIONS.find(
                          (option) => option.value === selectedValue
                        )?.label || selectedValue
                      );
                    }}
                    MenuProps={{
                      PaperProps: { style: { width: 280, borderRadius: 8 } },
                      MenuListProps: {
                        style: { paddingTop: 8, paddingBottom: 8 },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                    onDelete={null}>
                    {TIMEFRAME_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.timeframe && (
                    <FormHelperText error>Timeframe is required</FormHelperText>
                  )}
                </FormControl>
                {formData.timeframe === ReportTimeframe.CUSTOM_TIME_RANGE && (
                  <>
                    <DatePicker
                      open={fromOpen}
                      onClose={() => setFromOpen(false)}
                      value={formData.fromDate ?? null}
                      onChange={(date) => {
                        handleInputChange('fromDate', date);
                        setFromOpen(false);
                      }}
                      components={{ OpenPickerIcon: () => null }}
                      PopperProps={{
                        placement: 'bottom-start',
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className={classes.dateInput}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: formData.fromDate,
                            style: { textAlign: 'right' },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  onClick={() => setFromOpen(true)}>
                                  From
                                </Typography>
                              </InputAdornment>
                            ),
                            endAdornment: null,
                          }}
                        />
                      )}
                    />
                    <DatePicker
                      open={toOpen}
                      onClose={() => setToOpen(false)}
                      value={
                        formData.toDate
                          ? getDateOnly((formData.toDate as Date).toISOString())
                          : null
                      }
                      onChange={(date) => {
                        if (new Date(date) > dataAsOfDate) {
                          ToastUtils.error(
                            'To date cannot be in the future.',
                            true
                          );
                          return;
                        }
                        handleInputChange('toDate', date);
                        setToOpen(false);
                      }}
                      components={{ OpenPickerIcon: () => null }}
                      PopperProps={{
                        placement: 'bottom-start',
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className={classes.dateInput}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: formData.toDate,
                            style: { textAlign: 'right' },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  onClick={() => setToOpen(true)}>
                                  To
                                </Typography>
                              </InputAdornment>
                            ),
                            endAdornment: null,
                          }}
                        />
                      )}
                    />
                  </>
                )}
              </Box>
            )}
            {!!formData.reportType && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Data available as of{' '}
                  {dataAsOfDate ? getDateOnly(dataAsOfDate.toISOString()) : ''}
                </Typography>

                <Typography variant="body1" color="text.primary">
                  Give your report a name so you can easily find it later.
                </Typography>
                <TextField
                  id="custom-report-name"
                  name="custom-report-name"
                  aria-label="Custom report name"
                  placeholder={
                    'e.g. March 2025 Transaction Report or Jane Doe Account Report'
                  }
                  value={formData.reportName}
                  className={classes.reportNameField}
                  onChange={(e) => {
                    handleInputChange('reportName', e.target.value);
                  }}
                  error={
                    formData.reportName
                      ? formData.reportName.length > 100
                      : false
                  }
                  helperText={
                    formData.reportName && formData.reportName.length > 100
                      ? 'Report name must be 100 characters or less'
                      : ''
                  }
                />
              </>
            )}

            <ConfirmationModal
              open={isConfirmationModalOpen}
              onClose={handleCloseConfirmation}
              title={
                isGenerateReportSuccess
                  ? 'Report Generated'
                  : 'Error Generating Report'
              }
              content={message}
              modalType={isGenerateReportSuccess ? 'success' : 'error'}
            />
          </Box>
          <DialogActions className={classes.action}>
            <Button
              variant="outlined"
              onClick={handleClose}
              data-test-id="generate-report-cancel-button">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!isFormValid}
              data-test-id="generate-report-generate-button">
              Generate Report
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  );
};

export default GenerateReportModal;
